/**
 * AI & Automation Tab
 *
 * AI model selection, AI cost confirmation, usage/billing,
 * time estimation, task classification, voice transcription.
 * Merges content from CoreSettings (model, cost) and AdvancedSettings (enrichment).
 */

import { useState, useEffect } from 'react';
import clsx from 'clsx';
import {
  Bot,
  DollarSign,
  Check,
  Loader2,
  AlertCircle,
  Clock,
  Tags,
  RefreshCw,
  Mic,
  ChevronRight,
  ChevronDown,
  BarChart3,
  Sparkles,
} from 'lucide-react';
import {
  getAvailableModels,
  getSelectedModel,
  setSelectedModel,
  getEnrichmentStats,
  type AIModel,
} from '../../../api/client';
import {
  getUserSettings,
  updateAiCostSettings,
  updateAutoDecomposeSetting,
  type AutoDecomposeMode,
} from '../../../api/userSettings';
import {
  enqueueTimeEstimates,
  enqueueClassification,
  pollJobGroup,
  cancelJobGroup,
  fetchAiCostEstimate,
  type AiCostEstimate,
} from '../../../api/ai';
import { useSettings } from '../../../hooks/useSettings';
import { UsageCard } from '../../UsageCard';
import { BillingCard } from '../../BillingCard';
import type { EnrichmentStats } from '../../../types';

export function AiAutomationTab() {
  const { settings, updateTranscriptionMethod, updateTranscriptionVocabulary } = useSettings();

  // AI model selection
  const [availableModels, setAvailableModels] = useState<AIModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [isSavingModel, setIsSavingModel] = useState(false);

  // AI cost settings
  const [aiCostMode, setAiCostMode] = useState<'always' | 'above_threshold' | 'never'>('above_threshold');
  const [aiCostThreshold, setAiCostThreshold] = useState('0.50');
  const [isSavingAiCost, setIsSavingAiCost] = useState(false);
  const [aiCostSaveSuccess, setAiCostSaveSuccess] = useState(false);

  // Auto-decompose settings
  const [autoDecompose, setAutoDecompose] = useState<AutoDecomposeMode>('off');
  const [isSavingAutoDecompose, setIsSavingAutoDecompose] = useState(false);
  const [autoDecomposeSaveSuccess, setAutoDecomposeSaveSuccess] = useState(false);

  // Enrichment stats
  const [enrichmentStats, setEnrichmentStats] = useState<EnrichmentStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Time estimation
  const [isGeneratingEstimates, setIsGeneratingEstimates] = useState(false);
  const [estimateGroupId, setEstimateGroupId] = useState<string | null>(null);
  const [estimateProgress, setEstimateProgress] = useState<{ completed: number; total: number; pending?: number } | null>(null);
  const [estimateBatchSize, setEstimateBatchSize] = useState(10);
  const [showEstimateInstructions, setShowEstimateInstructions] = useState(false);
  const [estimateInstructions, setEstimateInstructions] = useState('');

  // Classification
  const [isClassifying, setIsClassifying] = useState(false);
  const [classifyGroupId, setClassifyGroupId] = useState<string | null>(null);
  const [classifyProgress, setClassifyProgress] = useState<{ completed: number; total: number; pending?: number } | null>(null);
  const [classifyBatchSize, setClassifyBatchSize] = useState(10);
  const [showClassifyInstructions, setShowClassifyInstructions] = useState(false);
  const [classifyInstructions, setClassifyInstructions] = useState('');

  // AI cost estimate confirmation modal
  const [pendingAction, setPendingAction] = useState<null | { label: string; proceed: () => Promise<void> }>(null);
  const [costEstimate, setCostEstimate] = useState<AiCostEstimate | null>(null);
  const [isEstimating, setIsEstimating] = useState(false);

  // UI
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      getAvailableModels().then(setAvailableModels),
      getSelectedModel().then((m) => setSelectedModelId(m.modelId)),
      getUserSettings().then((s) => {
        setAiCostMode(s.aiCostConfirmMode);
        setAiCostThreshold(s.aiCostThreshold.toFixed(2));
        setAutoDecompose(s.autoDecompose ?? 'off');
      }),
      loadEnrichmentStats(),
    ])
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const loadEnrichmentStats = async () => {
    setIsLoadingStats(true);
    setStatsError(null);
    try {
      const stats = await getEnrichmentStats();
      setEnrichmentStats(stats);
    } catch {
      setStatsError('Could not load stats');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const friendlyError = (err: unknown): string => {
    const msg: string = (err as Error).message || '';
    if (msg.includes('API key not configured') || msg.includes('unavailable')) {
      return 'AI features require a Claude API key. Set CLAUDE_API_KEY in your server environment.';
    }
    return msg;
  };

  const handleSaveModel = async () => {
    if (!selectedModelId) return;
    setIsSavingModel(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await setSelectedModel(selectedModelId);
      setSuccess(`AI model updated to ${result.model.name}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(`Failed to save model: ${(err as Error).message}`);
    } finally {
      setIsSavingModel(false);
    }
  };

  const handleSaveAiCostSettings = async () => {
    setIsSavingAiCost(true);
    setAiCostSaveSuccess(false);
    try {
      await updateAiCostSettings({
        aiCostConfirmMode: aiCostMode,
        aiCostThreshold: parseFloat(aiCostThreshold) || 0.5,
      });
      setAiCostSaveSuccess(true);
      setTimeout(() => setAiCostSaveSuccess(false), 3000);
    } catch {
      setError('Failed to save AI cost settings');
    } finally {
      setIsSavingAiCost(false);
    }
  };

  const handleSaveAutoDecompose = async (mode: AutoDecomposeMode) => {
    setAutoDecompose(mode);
    setIsSavingAutoDecompose(true);
    setAutoDecomposeSaveSuccess(false);
    try {
      await updateAutoDecomposeSetting(mode);
      setAutoDecomposeSaveSuccess(true);
      setTimeout(() => setAutoDecomposeSaveSuccess(false), 3000);
    } catch {
      setError('Failed to save auto-decompose setting');
      // Revert on error (re-read not needed — the old value was already in state before we set it)
    } finally {
      setIsSavingAutoDecompose(false);
    }
  };

  const runWithCostCheck = async (
    operation: 'classify' | 'embed' | 'insights' | 'brain-dump' | 'decompose',
    scope: 'single' | 'all',
    taskCount: number,
    label: string,
    action: () => Promise<void>,
  ) => {
    setIsEstimating(true);
    try {
      const estimate = await fetchAiCostEstimate({ operation, scope, taskCount });
      if (estimate.requiresConfirmation) {
        setCostEstimate(estimate);
        setPendingAction({ label, proceed: action });
      } else {
        await action();
      }
    } catch {
      await action();
    } finally {
      setIsEstimating(false);
    }
  };

  const handleGenerateEstimates = async () => {
    await runWithCostCheck('classify', 'all', estimateBatchSize, 'Generate Time Estimates', async () => {
      setIsGeneratingEstimates(true);
      setError(null);
      setSuccess(null);
      setEstimateProgress(null);
      setEstimateGroupId(null);

      try {
        const enqueued = await enqueueTimeEstimates({
          batchSize: estimateBatchSize,
          customInstructions: estimateInstructions || undefined,
        });

        if (!enqueued.groupId || enqueued.enqueued === 0) {
          setSuccess('All tasks already have time estimates.');
          setIsGeneratingEstimates(false);
          return;
        }

        const expectedTotal = enqueued.enqueued;
        setEstimateGroupId(enqueued.groupId);
        setEstimateProgress({ completed: 0, total: expectedTotal });

        await new Promise((r) => setTimeout(r, 500));

        let polls = 0;
        const maxPolls = 600;
        let status = await pollJobGroup(enqueued.groupId);
        while (status.completed + status.failed < expectedTotal && polls < maxPolls) {
          setEstimateProgress({ completed: status.completed, total: expectedTotal, pending: status.pending });
          await new Promise((r) => setTimeout(r, 1000));
          status = await pollJobGroup(enqueued.groupId);
          polls++;
        }
        const timedOut = polls >= maxPolls && status.completed + status.failed < expectedTotal;
        setEstimateProgress({ completed: status.completed, total: expectedTotal, pending: status.pending });
        if (timedOut) {
          setSuccess(`Estimation is processing in the background. ${status.completed} done so far.`);
        } else {
          setSuccess(`Generated estimates for ${status.completed} tasks`);
        }
        setTimeout(() => setEstimateProgress(null), 3000);
        await loadEnrichmentStats();
      } catch (err: unknown) {
        setError(friendlyError(err));
      } finally {
        setIsGeneratingEstimates(false);
      }
    });
  };

  const handleCancelEstimates = async () => {
    if (!estimateGroupId) return;
    try {
      await cancelJobGroup(estimateGroupId);
      setIsGeneratingEstimates(false);
      setEstimateProgress(null);
      setEstimateGroupId(null);
    } catch {
      // Ignore cancel errors
    }
  };

  const handleClassifyTasks = async () => {
    await runWithCostCheck('classify', 'all', classifyBatchSize, 'Classify Tasks', async () => {
      setIsClassifying(true);
      setError(null);
      setSuccess(null);
      setClassifyProgress(null);
      setClassifyGroupId(null);

      try {
        const enqueued = await enqueueClassification({
          batchSize: classifyBatchSize,
          customInstructions: classifyInstructions || undefined,
        });

        if (!enqueued.groupId || enqueued.enqueued === 0) {
          setSuccess('All tasks are already classified.');
          setIsClassifying(false);
          return;
        }

        const expectedTotal = enqueued.enqueued;
        setClassifyGroupId(enqueued.groupId);
        setClassifyProgress({ completed: 0, total: expectedTotal });

        await new Promise((r) => setTimeout(r, 500));

        let polls = 0;
        const maxPolls = 600;
        let status = await pollJobGroup(enqueued.groupId);
        while (status.completed + status.failed < expectedTotal && polls < maxPolls) {
          setClassifyProgress({ completed: status.completed, total: expectedTotal, pending: status.pending });
          await new Promise((r) => setTimeout(r, 1000));
          status = await pollJobGroup(enqueued.groupId);
          polls++;
        }
        const timedOut = polls >= maxPolls && status.completed + status.failed < expectedTotal;
        setClassifyProgress({ completed: status.completed, total: expectedTotal, pending: status.pending });
        if (timedOut) {
          setSuccess(`Classification is processing in the background. ${status.completed} done so far.`);
        } else {
          setSuccess(`Classified ${status.completed} of ${expectedTotal} tasks`);
        }
        setTimeout(() => setClassifyProgress(null), 3000);
        await loadEnrichmentStats();
      } catch (err: unknown) {
        setError(friendlyError(err));
      } finally {
        setIsClassifying(false);
      }
    });
  };

  const handleCancelClassify = async () => {
    if (!classifyGroupId) return;
    try {
      await cancelJobGroup(classifyGroupId);
      setIsClassifying(false);
      setClassifyProgress(null);
      setClassifyGroupId(null);
    } catch {
      // Ignore cancel errors
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <>
      {/* AI Cost Confirmation Modal */}
      {costEstimate && pendingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-xl shadow-xl p-6 max-w-sm w-full mx-4 space-y-4">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary-500" />
              <h3 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
                Confirm AI Operation
              </h3>
            </div>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              <strong>{pendingAction.label}</strong> will cost approximately{' '}
              <span className="font-mono font-semibold text-text-primary-light dark:text-text-primary-dark">
                {costEstimate.estimatedCostFormatted}
              </span>{' '}
              and take ~{costEstimate.estimatedDurationSeconds}s.
            </p>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
              {costEstimate.tokens.input.toLocaleString()} input + {costEstimate.tokens.output.toLocaleString()} output tokens · {costEstimate.model}
            </p>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => { setCostEstimate(null); setPendingAction(null); }}
                className="flex-1 px-4 py-2 text-sm font-medium border border-border-subtle-light dark:border-border-subtle-dark text-text-primary-light dark:text-text-primary-dark rounded-lg hover:bg-surface-raised-light dark:hover:bg-surface-raised-dark transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const action = pendingAction.proceed;
                  setCostEstimate(null);
                  setPendingAction(null);
                  await action();
                }}
                className="flex-1 px-4 py-2 text-sm font-medium bg-primary-700 hover:bg-primary-800 text-white rounded-lg transition-colors"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
        </div>
      )}

      {/* AI Model Selection */}
      {availableModels.length > 0 && (
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
              AI Model
            </h2>
          </div>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            Choose which AI model Danny uses for chat. Danny proxies directly to each provider and applies a small markup to cover infrastructure costs.
          </p>

          {(['anthropic', 'openai', 'google'] as const).map((provider) => {
            const providerModels = availableModels.filter((m) => m.provider === provider);
            if (providerModels.length === 0) return null;

            const providerLabel: Record<string, string> = {
              anthropic: 'Anthropic',
              openai: 'OpenAI',
              google: 'Google',
            };

            return (
              <div key={provider} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary-dark">
                  {providerLabel[provider]}
                </p>
                <div className="space-y-2">
                  {providerModels.map((model) => {
                    const isSelected = selectedModelId === model.id;
                    return (
                      <button
                        key={model.id}
                        type="button"
                        onClick={() => setSelectedModelId(model.id)}
                        className={`w-full text-left p-4 border-2 rounded-lg transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-border-subtle-light dark:border-border-subtle-dark hover:border-border-hover-light dark:hover:border-border-hover-dark'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                                {model.name}
                              </span>
                              {model.isDefault && (
                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded">
                                  Default
                                </span>
                              )}
                              {isSelected && (
                                <Check className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                              {model.description}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-text-tertiary-light dark:text-text-secondary-dark whitespace-nowrap">
                              ${model.inputCostPerMillion} / ${model.outputCostPerMillion}
                            </p>
                            <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                              per M tokens in/out
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <button
            onClick={handleSaveModel}
            disabled={isSavingModel || !selectedModelId}
            className="w-full px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSavingModel ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Model
              </>
            )}
          </button>
        </div>
      )}

      {/* AI Usage & Billing */}
      <UsageCard />
      <BillingCard />

      {/* AI Cost Confirmation Settings */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            AI Cost Confirmation
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Control when Danny asks for confirmation before running AI batch operations.
        </p>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                Ask for confirmation
              </p>
            </div>
            <select
              value={aiCostMode}
              onChange={(e) => setAiCostMode(e.target.value as typeof aiCostMode)}
              className="px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="above_threshold">Above threshold</option>
              <option value="always">Always</option>
              <option value="never">Never</option>
            </select>
          </div>

          {aiCostMode === 'above_threshold' && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                  Threshold (USD)
                </p>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                  Confirm when estimated cost exceeds this amount
                </p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.10"
                  value={aiCostThreshold}
                  onChange={(e) => setAiCostThreshold(e.target.value)}
                  className="w-20 px-3 py-1.5 text-sm bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            {aiCostSaveSuccess && (
              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                <Check className="w-3.5 h-3.5" />
                Saved
              </span>
            )}
            <button
              onClick={handleSaveAiCostSettings}
              disabled={isSavingAiCost}
              className="px-4 py-2 text-sm font-medium bg-primary-700 hover:bg-primary-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSavingAiCost ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Auto-Decompose Tasks */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Auto-decompose tasks</h2>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Danny analyzes new tasks and (if enabled) suggests breaking them into smaller pieces.
              You always review and confirm before subtasks are saved.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {([
            { value: 'off' as const, label: 'Off', description: 'Never automatically suggest breaking down tasks.', recommended: false },
            { value: 'suggest' as const, label: 'Suggest', description: 'Show a hint when Danny thinks a task could be broken down.', recommended: true },
            { value: 'auto' as const, label: 'Auto', description: 'Danny will automatically suggest subtasks for any new task. You confirm before saving.', recommended: false },
          ] as const).map((option) => (
            <label
              key={option.value}
              className={clsx(
                'flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                autoDecompose === option.value
                  ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                  : 'border-border-subtle-light dark:border-border-subtle-dark hover:border-border-hover-light dark:hover:border-border-hover-dark'
              )}
            >
              <input
                type="radio"
                name="autoDecompose"
                value={option.value}
                checked={autoDecompose === option.value}
                onChange={() => handleSaveAutoDecompose(option.value)}
                disabled={isSavingAutoDecompose}
                className="mt-0.5 accent-primary-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    {option.label}
                  </span>
                  {option.recommended && (
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                  {option.description}
                </p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-1">
          {isSavingAutoDecompose && (
            <span className="flex items-center gap-1 text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
          {autoDecomposeSaveSuccess && (
            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <Check className="w-3.5 h-3.5" />
              Saved
            </span>
          )}
        </div>
      </div>

      {/* Enrichment Stats Summary */}
      {isLoadingStats ? (
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="h-5 w-40 bg-surface-hover-light dark:bg-surface-hover-dark rounded animate-pulse" />
            <div className="h-8 w-8 bg-surface-hover-light dark:bg-surface-hover-dark rounded-lg animate-pulse" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="text-center p-3 bg-surface-base-light dark:bg-surface-hover-dark rounded-lg space-y-2">
                <div className="h-8 w-12 bg-border-subtle-light dark:bg-border-hover-dark rounded mx-auto animate-pulse" />
                <div className="h-3 w-16 bg-surface-hover-light dark:bg-surface-hover-dark rounded mx-auto animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ) : statsError ? (
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark">{statsError}</span>
          <button onClick={loadEnrichmentStats} className="p-1.5 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
          </button>
        </div>
      ) : enrichmentStats && (
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Enrichment Overview</h2>
            <button onClick={loadEnrichmentStats} className="p-2 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors">
              <RefreshCw className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-surface-base-light dark:bg-surface-hover-dark rounded-lg">
              <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{enrichmentStats.total}</div>
              <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Total Tasks</div>
            </div>
            <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{enrichmentStats.classified}</div>
              <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Classified</div>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{enrichmentStats.withTimeEstimate}</div>
              <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">With Estimates</div>
            </div>
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{Math.round(enrichmentStats.classificationRate)}%</div>
              <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Coverage</div>
            </div>
          </div>
        </div>
      )}

      {/* Time Estimation Section */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Time Estimation</h2>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">AI estimates how long each task will take</p>
          </div>
        </div>

        {(() => {
          const needsEstimates = enrichmentStats ? enrichmentStats.total - enrichmentStats.withTimeEstimate : null;
          const allDone = needsEstimates === 0;
          const toProcess = needsEstimates != null ? Math.min(estimateBatchSize, needsEstimates) : estimateBatchSize;
          return (
            <>
              <div className="flex gap-2">
                <select
                  value={estimateBatchSize}
                  onChange={(e) => setEstimateBatchSize(Number(e.target.value))}
                  disabled={isGeneratingEstimates || isEstimating || allDone}
                  className="px-3 py-2 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-hover-light dark:border-border-subtle-dark rounded-lg text-sm text-text-secondary-light dark:text-text-primary-dark disabled:opacity-50"
                >
                  <option value={10}>10 at a time</option>
                  <option value={25}>25 at a time</option>
                  <option value={50}>50 at a time</option>
                </select>
                <button
                  onClick={handleGenerateEstimates}
                  disabled={isGeneratingEstimates || isEstimating || allDone}
                  className="flex-1 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
                >
                  {isGeneratingEstimates ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Estimating...</>
                  ) : allDone ? (
                    <><Check className="w-4 h-4" />All estimated</>
                  ) : (
                    <><Clock className="w-4 h-4" />Estimate {needsEstimates != null ? `${toProcess} of ${needsEstimates}` : toProcess} tasks</>
                  )}
                </button>
              </div>

              <button
                onClick={() => setShowEstimateInstructions((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark/50 transition-colors border border-transparent hover:border-border-subtle-light dark:hover:border-border-hover-dark"
              >
                {showEstimateInstructions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Custom instructions
                {estimateInstructions && <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />}
              </button>
              {showEstimateInstructions && (
                <textarea
                  value={estimateInstructions}
                  onChange={(e) => setEstimateInstructions(e.target.value)}
                  placeholder="e.g. I work slowly due to chronic fatigue -- add 50% to all estimates"
                  rows={3}
                  className="w-full px-3 py-2 bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-sm text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              )}
            </>
          );
        })()}

        {estimateProgress && (
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary-light dark:text-text-secondary-dark">
                {isGeneratingEstimates
                  ? `Estimating... ${estimateProgress.completed} / ${estimateProgress.total}${estimateProgress.pending ? ` (${estimateProgress.pending} pending)` : ''}`
                  : estimateProgress.completed === 0
                    ? `Jobs queued — estimates run in the background`
                    : `✓ ${estimateProgress.completed} of ${estimateProgress.total} estimated`}
              </span>
              {isGeneratingEstimates && (
                <button onClick={handleCancelEstimates} className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors">
                  Cancel
                </button>
              )}
            </div>
            <div className="h-2 bg-surface-hover-light dark:bg-surface-hover-dark rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${estimateProgress.total > 0 ? Math.round((estimateProgress.completed / estimateProgress.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Task Classification Section */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <Tags className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Task Classification</h2>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Automatically categorize your tasks</p>
          </div>
        </div>

        {(() => {
          const unclassified = enrichmentStats?.unclassified ?? null;
          const allDone = unclassified === 0;
          const toProcess = unclassified != null ? Math.min(classifyBatchSize, unclassified) : classifyBatchSize;
          return (
            <>
              <div className="flex gap-2">
                <select
                  value={classifyBatchSize}
                  onChange={(e) => setClassifyBatchSize(Number(e.target.value))}
                  disabled={isClassifying || isEstimating || allDone}
                  className="px-3 py-2 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-hover-light dark:border-border-subtle-dark rounded-lg text-sm text-text-secondary-light dark:text-text-primary-dark disabled:opacity-50"
                >
                  <option value={10}>10 at a time</option>
                  <option value={25}>25 at a time</option>
                  <option value={50}>50 at a time</option>
                </select>
                <button
                  onClick={handleClassifyTasks}
                  disabled={isClassifying || isEstimating || allDone}
                  className="flex-1 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
                >
                  {isClassifying ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Classifying...</>
                  ) : allDone ? (
                    <><Check className="w-4 h-4" />All classified</>
                  ) : (
                    <><Tags className="w-4 h-4" />Classify {unclassified != null ? `${toProcess} of ${unclassified}` : toProcess} tasks</>
                  )}
                </button>
              </div>

              <button
                onClick={() => setShowClassifyInstructions((v) => !v)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark/50 transition-colors border border-transparent hover:border-border-subtle-light dark:hover:border-border-hover-dark"
              >
                {showClassifyInstructions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Custom instructions
                {classifyInstructions && <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />}
              </button>
              {showClassifyInstructions && (
                <textarea
                  value={classifyInstructions}
                  onChange={(e) => setClassifyInstructions(e.target.value)}
                  placeholder="e.g. Most home tasks are shared with my spouse, classify those as personal-family"
                  rows={3}
                  className="w-full px-3 py-2 bg-surface-base-light dark:bg-surface-hover-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-sm text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              )}
            </>
          );
        })()}

        {classifyProgress && (
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary-light dark:text-text-secondary-dark">
                {isClassifying
                  ? `Classifying... ${classifyProgress.completed} / ${classifyProgress.total}${classifyProgress.pending ? ` (${classifyProgress.pending} pending)` : ''}`
                  : classifyProgress.completed === 0
                    ? `Jobs queued — classification runs in the background`
                    : `✓ ${classifyProgress.completed} of ${classifyProgress.total} classified`}
              </span>
              {isClassifying && (
                <button onClick={handleCancelClassify} className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors">
                  Cancel
                </button>
              )}
            </div>
            <div className="h-2 bg-surface-hover-light dark:bg-surface-hover-dark rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-500"
                style={{ width: `${classifyProgress.total > 0 ? Math.round((classifyProgress.completed / classifyProgress.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Category Breakdown */}
      {enrichmentStats && Object.keys(enrichmentStats.byCategory).length > 0 && (
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Category Breakdown</h2>
          </div>
          <div className="space-y-2">
            {Object.entries(enrichmentStats.byCategory)
              .sort(([, a], [, b]) => b - a)
              .map(([category, count]) => {
                const percentage = Math.round((count / enrichmentStats.total) * 100);
                return (
                  <div key={category} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">{category}</span>
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-surface-hover-light dark:bg-surface-hover-dark rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 dark:bg-purple-400 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Voice Transcription Settings */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Mic className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Voice Transcription</h2>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Configure how voice input is transcribed</p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
            Transcription Method
          </label>
          <select
            value={settings.transcriptionMethod || 'local-tiny'}
            onChange={(e) => updateTranscriptionMethod(e.target.value as 'local-tiny' | 'local-base' | 'local-small' | 'api')}
            className="w-full px-3 py-2 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-hover-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="local-tiny">Local - Whisper Tiny (39MB, fastest)</option>
            <option value="local-base">Local - Whisper Base (74MB, balanced)</option>
            <option value="local-small">Local - Whisper Small (244MB, best quality)</option>
            <option value="api">API - OpenAI Whisper (requires API key)</option>
          </select>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            Local models run in your browser (offline, free, private). API uses OpenAI (requires internet & API key).
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
            Custom Vocabulary (Optional)
          </label>
          <textarea
            value={settings.transcriptionVocabulary || ''}
            onChange={(e) => updateTranscriptionVocabulary(e.target.value)}
            placeholder="e.g., Ardis, Kubernetes, PostgreSQL, OpenAI"
            rows={2}
            disabled={settings.transcriptionMethod !== 'api'}
            className="w-full px-3 py-2 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-hover-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
            {settings.transcriptionMethod === 'api'
              ? 'Add words that are often misheard (names, technical terms, etc.). Comma-separated.'
              : 'Custom vocabulary only works with API method. Local models do not support this feature yet.'}
          </p>
        </div>
      </div>
    </>
  );
}
